import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { post, get } from "../../lib/request";

export const createAlert = createAsyncThunk(
  "alert/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await post("alerts", payload);
      return { ...res, _requestPayload: payload };
    } catch (err) {
      console.error("❌ createAlert error:", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

function normalizeAlertDetailResponse(res) {
  if (res == null) return null;
  if (
    typeof res === "object" &&
    !Array.isArray(res) &&
    res.data != null &&
    typeof res.data === "object" &&
    !Array.isArray(res.data)
  ) {
    return res.data;
  }
  return res;
}

/** GET /api/v1/alerts/{alertId} — alert id comes from message payload (e.g. GET /messages/{id} → alertId). */
export const fetchAlertById = createAsyncThunk(
  "alert/fetchById",
  async (alertId, { rejectWithValue }) => {
    try {
      const id = alertId != null ? String(alertId).trim() : "";
      if (!id) {
        return rejectWithValue("alertId is required");
      }
      const res = await get(`alerts/${id}`);
      return normalizeAlertDetailResponse(res);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const alertSlice = createSlice({
  name: "alert",
  initialState: {
    loading: false,
    success: false,
    error: null,
    createdAlerts: {},
    /** Parent `messageId` from create payload → ordered list of { alertId, ts, … } for multi-ALERT under same parent. */
    createdAlertHistory: {},
    alertDetail: null,
    alertDetailLoading: false,
  },
  reducers: {
    resetAlertState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.alertDetail = null;
      state.createdAlerts = {};
      state.createdAlertHistory = {};
    },
    clearAlertDetail: (state) => {
      state.alertDetail = null;
    },
    /** Set alert detail from GET /messages/{interactionId} nested `alerts` or create payload — no GET /alert/{id}. */
    hydrateAlertDetail: (state, action) => {
      state.alertDetailLoading = false;
      state.alertDetail = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAlert.pending, (state) => {
        state.loading = true;
        state.success = false;
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const requestPayload = action.payload?._requestPayload;
        const data = action.payload?.data;
        if (requestPayload?.messageId) {
          const merged = {
            ...requestPayload,
            ...(data && typeof data === "object" ? data : {}),
          };
          if (data?.id != null) merged.alertId = data.id;
          const key = String(requestPayload.messageId);
          state.createdAlerts[key] = merged;
          if (!state.createdAlertHistory[key]) state.createdAlertHistory[key] = [];
          state.createdAlertHistory[key].push({
            ...merged,
            alertId: data?.id ?? merged.alertId,
            ts: Date.now(),
          });
        }
      })
      .addCase(createAlert.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAlertById.pending, (state) => {
        state.alertDetailLoading = true;
      })
      .addCase(fetchAlertById.fulfilled, (state, action) => {
        state.alertDetailLoading = false;
        state.alertDetail = action.payload;
      })
      .addCase(fetchAlertById.rejected, (state) => {
        state.alertDetailLoading = false;
        state.alertDetail = null;
      });
  },
});

export const { resetAlertState, clearAlertDetail, hydrateAlertDetail } = alertSlice.actions;
export default alertSlice.reducer;
